import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle } from "lucide-react";
import menuService from "@/services/menuService";
import profileService from "@/services/profileService";

const FieldError = ({ message }) =>
  message ? (
    <div className="flex items-center gap-1 text-red-500 text-sm">
      <AlertCircle className="w-4 h-4" />
      <span>{message}</span>
    </div>
  ) : null;

/**
 * Modal para criar ou editar item do menu lateral
 */
export default function MenuFormModal({
  open,
  onOpenChange,
  menu,
  profiles = [],
  nextOrder = 1,
  onSave,
  loading,
}) {
  const isEditing = !!menu;
  const iconNames = menuService.getIconNames();

  const [formData, setFormData] = useState({
    label: "",
    link: "",
    icon: "",
    order: "",
    enabled: true,
    roles: [],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (menu) {
      setFormData({
        label: menu.label || "",
        link: menu.link || "",
        icon: menu.icon || "",
        order: menu.order ?? 0,
        enabled: menu.enabled !== false,
        roles: (menu.roles || []).map((r) => profileService.normalizeName(r)),
      });
    } else {
      setFormData({
        label: "",
        link: "",
        icon: "",
        order: nextOrder,
        enabled: true,
        roles: [],
      });
    }
    setErrors({});
  }, [menu, open, nextOrder]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleToggleRole = (role, checked) => {
    handleChange(
      "roles",
      checked
        ? [...formData.roles, role]
        : formData.roles.filter((r) => r !== role),
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = menuService.validateMenuData(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    onSave({
      label: formData.label.trim(),
      link: formData.link.trim(),
      icon: formData.icon.trim(),
      order: Number(formData.order),
      enabled: formData.enabled,
      roles: formData.roles,
    });
  };

  // Ícone atual pode não estar no mapa (cadastrado direto no backend)
  const iconOptions =
    formData.icon && !iconNames.includes(formData.icon)
      ? [formData.icon, ...iconNames]
      : iconNames;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar Menu" : "Novo Menu"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize o item do menu lateral. O link não pode ser alterado."
              : "Cadastre um item do menu lateral do painel."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="menu-label">
              Nome <span className="text-red-500">*</span>
            </Label>
            <Input
              id="menu-label"
              placeholder="Ex.: Pedidos"
              value={formData.label}
              onChange={(e) => handleChange("label", e.target.value)}
              className={errors.label ? "border-red-500" : ""}
              disabled={loading}
            />
            <FieldError message={errors.label} />
          </div>

          {/* Link */}
          <div className="space-y-2">
            <Label htmlFor="menu-link">
              Link (chave da tela) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="menu-link"
              placeholder="Ex.: orders"
              value={formData.link}
              onChange={(e) =>
                handleChange("link", e.target.value.toLowerCase())
              }
              className={`font-mono ${errors.link ? "border-red-500" : ""}`}
              disabled={loading || isEditing}
            />
            {errors.link ? (
              <FieldError message={errors.link} />
            ) : (
              <p className="text-xs text-gray-500">
                {isEditing
                  ? "O link identifica a tela no painel e não pode ser alterado."
                  : "Precisa ser igual ao identificador da tela no painel (letras minúsculas, números e hífen)."}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Ícone */}
            <div className="space-y-2">
              <Label htmlFor="menu-icon">
                Ícone <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => handleChange("icon", value)}
                disabled={loading}
              >
                <SelectTrigger
                  id="menu-icon"
                  className={errors.icon ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((name) => {
                    const IconComponent = menuService.resolveIcon(name);
                    return (
                      <SelectItem key={name} value={name}>
                        <span className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          {name}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <FieldError message={errors.icon} />
            </div>

            {/* Ordem */}
            <div className="space-y-2">
              <Label htmlFor="menu-order">
                Ordem <span className="text-red-500">*</span>
              </Label>
              <Input
                id="menu-order"
                type="number"
                min="0"
                step="1"
                value={formData.order}
                onChange={(e) => handleChange("order", e.target.value)}
                className={errors.order ? "border-red-500" : ""}
                disabled={loading}
              />
              <FieldError message={errors.order} />
            </div>
          </div>

          {/* Perfis */}
          <div className="space-y-2">
            <Label>Perfis com acesso</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-gray-200 p-3">
              {profiles.length === 0 ? (
                <p className="text-sm text-gray-500 sm:col-span-2">
                  Nenhum perfil cadastrado.
                </p>
              ) : (
                profiles.map((profile) => {
                  const value = profileService.normalizeName(profile.name);
                  return (
                    <label
                      key={profile.id}
                      htmlFor={`menu-role-${value}`}
                      className="flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <Checkbox
                        id={`menu-role-${value}`}
                        checked={formData.roles.includes(value)}
                        onCheckedChange={(checked) =>
                          handleToggleRole(value, checked === true)
                        }
                        disabled={loading}
                      />
                      <span className="text-gray-900">
                        {profileService.getProfileLabel(value)}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <p
              className={`text-xs ${
                formData.roles.length === 0 ? "text-amber-600" : "text-gray-500"
              }`}
            >
              {formData.roles.length === 0
                ? "Sem perfil marcado, o menu fica visível para todos os usuários."
                : `${formData.roles.length} perfil(is) com acesso`}
            </p>
          </div>

          {/* Ativo */}
          <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
            <div>
              <Label htmlFor="menu-enabled">Menu ativo</Label>
              <p className="text-xs text-gray-500">
                Menus inativos não aparecem para nenhum usuário
              </p>
            </div>
            <Switch
              id="menu-enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) => handleChange("enabled", checked)}
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#1b5e20" }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.background = "#144919")
              }
              onMouseLeave={(e) =>
                !loading && (e.target.style.background = "#1b5e20")
              }
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
